/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { ACLRule, Engine, ParsedConfig } from './types';

function extractRules(aclText: string): ACLRule[] {
  const rules: ACLRule[] = [];

  // Simple pattern matching for common rule formats
  const rulePatterns = [
    /(\w+)\s+(\w+)/g, // "allow read", "deny write"
    /action:\s*"(\w+)".*resource:\s*"([^"]+)"/g,
  ];

  rulePatterns.forEach((pattern) => {
    const matches = Array.from(aclText.matchAll(pattern));

    matches.forEach((match) => {
      rules.push({
        action: match[1],
        resource: match[2],
      });
    });
  });

  return rules;
}

function extractEngines(enginesText: string): Engine[] {
  const engines: Engine[] = [];

  // Look for engine definitions
  const engineMatches = enginesText.match(/{\s*name:\s*"([^"]+)"[^}]*}/g);
  if (engineMatches) {
    engineMatches.forEach((engineMatch) => {
      const nameMatch = engineMatch.match(/name:\s*"([^"]+)"/);
      const typeMatch = engineMatch.match(/type:\s*"([^"]+)"/);
      const enabledMatch = engineMatch.match(/enabled:\s*(true|false)/);

      if (nameMatch) {
        engines.push({
          name: nameMatch[1],
          type: typeMatch?.[1] || 'unknown',
          enabled: enabledMatch?.[1] === 'true',
        });
      }
    });
  }

  return engines;
}

export function parseConfigSource(source: string): ParsedConfig {
  try {
    // Try to parse as JSON first
    const parsed = JSON.parse(source);

    // Handle ACLs configuration
    let aclProtection;
    if (parsed.acls?.rule_set) {
      aclProtection = {
        enabled: parsed.acls.rule_set.length > 0,
        rules: parsed.acls.rule_set.map((rule: Record<string, unknown>) => ({
          action: String(rule.permission || 'unknown').toLowerCase(),
          resource: `${rule.resource_type || 'unknown'}: ${rule.resource_descriptor || 'unknown'}`,
          actor: `${rule.actor_type || 'unknown'}: ${rule.actor_descriptor || 'unknown'}`,
          capability: String(rule.capability || ''),
          ruleId: Number(rule.rule_id) || 0,
        })),
      };
    } else if (parsed.acl_protection || parsed.aclProtection) {
      aclProtection = parsed.acl_protection || parsed.aclProtection;
    }

    // Handle engines configuration (convert object to array)
    let engines;
    if (parsed.engines && typeof parsed.engines === 'object' && !Array.isArray(parsed.engines)) {
      engines = Object.entries(parsed.engines).map(([name, config]) => {
        const engineConfig = config as Record<string, unknown>;
        return {
          name,
          type: name, // Use the engine name as type
          enabled: Boolean(engineConfig.active || engineConfig.enabled),
          config: engineConfig,
        };
      });
    } else if (Array.isArray(parsed.engines)) {
      engines = parsed.engines;
    }

    return {
      aclProtection,
      engines,
      general:
        parsed.general ||
        parsed.config ||
        (parsed.schema_version && {
          schema_version: parsed.schema_version,
          driver_flags: parsed.driver_flags,
          broker: parsed.broker,
        }),
      rawData: source,
    };
  } catch {
    // If not JSON, try to extract structured data from CUE or other formats
    const config: ParsedConfig = { rawData: source };

    // Look for ACL protection patterns
    const aclMatch = source.match(/acl_protection\s*:\s*{([^}]+)}/s);
    if (aclMatch) {
      config.aclProtection = {
        enabled: /enabled\s*:\s*true/i.test(aclMatch[1]),
        rules: extractRules(aclMatch[1]),
      };
    }

    // Look for engines patterns
    const enginesMatch = source.match(/engines\s*:\s*\[([^\]]+)\]/s);
    if (enginesMatch) {
      config.engines = extractEngines(enginesMatch[1]);
    }

    return config;
  }
}
